import { Component, HostBinding, Input } from '@angular/core';
import { LoButton, LoCard, LoIcon } from 'lo-design-system';
import { EnterprisePlan } from '@brand-lolly/app/billing/types/enterprise-plan.types';

/**
 * Enterprise tier card shown alongside the regular plan cards. Not purchasable:
 * shows custom-pricing copy + a checkmark feature list, and its CTA opens the
 * plan's Calendly link (provided by the backend) in a new tab.
 *
 * SEVEN GRID ROWS, matching `app-plan-card` exactly — head, subtitle,
 * price, price-note, dial, button, features — because both cards share the same
 * subgrid rows on the plans grid. That is what puts the quoted card's button on
 * the same line as the three priced ones without a min-height anywhere. See
 * `PlanCardComponent`'s class comment.
 *
 * WHERE THE TWO CARDS DIFFER: this one spans EIGHT rows to the plan card's
 * seven, and its seventh child spans the last two (`.plan-body { grid-row: span
 * 2 }`). The bullet list here is far longer than a plan card's four rows, and
 * while every card shared one final row it stretched all of them to its own
 * height. Splitting it across a row only this card spans leaves the priced cards
 * ending at their own content.
 */
@Component({
	standalone: true,
	selector: 'app-enterprise-card',
	templateUrl: './enterprise-card.component.html',
	styleUrls: ['./enterprise-card.component.scss'],
	imports: [LoButton, LoCard, LoIcon]
})
export class EnterpriseCardComponent {
	@Input() plan!: EnterprisePlan;

	/** The one line under the title. Empty on a product that has no copy for it
	 *  — the row still renders, because it is a subgrid row. */
	@Input() subtitle = '';
	/** The note under the price ("Annual contract, invoiced"). */
	@Input() priceNote = '';

	/** The dial's label and its fixed reading. Both empty leaves the dial row as
	 *  a spacer, which is what a product with no credit ladder wants. */
	@Input() dialLabel = '';
	@Input() dialValue = '';

	/**
	 * When rendered next to the Creator Data API slider cards, reserves the same
	 * volume+slider height in the dial row so the bullets line up. Those cards
	 * are laid out in a plain flex row rather than in the plans subgrid, so they
	 * have nothing else holding them level.
	 */
	@Input() sliderSpacer = false;

	/**
	 * Lay the card out across the full width of a row of its own (offer on the
	 * right, description and bullets on the left) instead of as one column of
	 * the plans grid. Off by default: the pricing pages keep the column card.
	 */
	@Input() wide = false;

	@HostBinding('class.enterprise-wide')
	get isWide(): boolean {
		return this.wide;
	}

	@HostBinding('class.enterprise-plan') readonly hostClass = true;

	@HostBinding('class.slider-spacer')
	get isSliderSpacer(): boolean {
		return this.sliderSpacer;
	}

	onContact(): void {
		if (this.plan?.calendly_url) {
			window.open(this.plan.calendly_url, '_blank');
		}
	}
}
